// src/components/LocationInputsForCheckout.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const GEO_BASE = "https://secure.geonames.org";
const safe = (a) => (Array.isArray(a) ? a : []);

export default function LocationInputsForCheckout({
  country,
  city,
  state: region,
  setCountry,
  setCity,
  setState,
  geonamesUser = "murataktas", // kendi GeoNames kullanıcı adını ver
  t = (k) => k, // i18n varsa t geç
  disabled = false, // checkout’ta “adres seçili” durumunda disable edebilmek için
}) {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [sugg, setSugg] = useState({ country: [], city: [], state: [] });

  const countryRef = useRef(null);
  const cityRef = useRef(null);
  const stateRef = useRef(null);

  // Ülkeler
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(
          `${GEO_BASE}/countryInfoJSON?username=${encodeURIComponent(
            geonamesUser || "demo"
          )}`
        );
        const data = await res.json();
        if (!cancel) setCountries(safe(data?.geonames));
      } catch (e) {
        console.error("Country fetch error:", e);
        if (!cancel) setCountries([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [geonamesUser]);

  // Seçili ülke
  const selectedCountry = useMemo(() => {
    const lookup = (country || "").trim().toLowerCase();
    return safe(countries).find((c) => c.countryName?.toLowerCase() === lookup);
  }, [country, countries]);

  // Şehirler
  useEffect(() => {
    let cancel = false;
    if (!selectedCountry?.countryCode) {
      setCities([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${GEO_BASE}/searchJSON?country=${
            selectedCountry.countryCode
          }&featureClass=P&maxRows=1000&orderby=population&username=${encodeURIComponent(
            geonamesUser || "demo"
          )}`
        );
        const data = await res.json();
        if (!cancel) setCities(safe(data?.geonames));
      } catch (e) {
        console.error("City fetch error:", e);
        if (!cancel) setCities([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [selectedCountry, geonamesUser]);

  // Seçili şehir
  const selectedCity = useMemo(() => {
    const lookup = (city || "").trim().toLowerCase();
    return safe(cities).find((c) => c.name?.toLowerCase() === lookup);
  }, [city, cities]);

  // State/Region
  useEffect(() => {
    let cancel = false;
    if (!selectedCity?.geonameId) {
      setStates([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${GEO_BASE}/childrenJSON?geonameId=${
            selectedCity.geonameId
          }&username=${encodeURIComponent(geonamesUser || "demo")}`
        );
        const data = await res.json();
        if (!cancel) setStates(safe(data?.geonames));
      } catch (e) {
        console.error("State fetch error:", e);
        if (!cancel) setStates([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [selectedCity, geonamesUser]);

  const openSugg = (name) => {
    if (disabled) return; // disable iken öneri açma
    if (name === "country") {
      const list = safe(countries)
        .filter((c) =>
          c.countryName?.toLowerCase().includes((country || "").toLowerCase())
        )
        .map((c) => c.countryName);
      setSugg((s) => ({ ...s, country: list.slice(0, 50) }));
    }
    if (name === "city") {
      const list = safe(cities)
        .filter((c) =>
          c.name?.toLowerCase().includes((city || "").toLowerCase())
        )
        .map((c) => c.name);
      setSugg((s) => ({ ...s, city: list.slice(0, 50) }));
    }
    if (name === "state") {
      const list = safe(states)
        .filter((x) =>
          x.name?.toLowerCase().includes((region || "").toLowerCase())
        )
        .map((x) => x.name);
      setSugg((s) => ({ ...s, state: list.slice(0, 50) }));
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        !countryRef.current?.contains(e.target) &&
        !cityRef.current?.contains(e.target) &&
        !stateRef.current?.contains(e.target)
      ) {
        setSugg({ country: [], city: [], state: [] });
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const Dropdown = ({ items, onSelect }) =>
    disabled ? null : (
      <div
        className="list-group position-absolute w-100"
        style={{ zIndex: 10, maxHeight: 240, overflow: "auto" }}
      >
        {safe(items).map((item, i) => (
          <button
            key={`${item}-${i}`}
            type="button"
            className="list-group-item list-group-item-action"
            onClick={() => onSelect(item)}
          >
            {item}
          </button>
        ))}
      </div>
    );

  return (
    <>
      {/* Country */}
      <div className="col-lg-4">
        <div className="mb-3 position-relative" ref={countryRef}>
          <input
            type="text"
            className="form-control fw-light p-3"
            placeholder={t("Country")}
            value={country || ""}
            onChange={(e) => {
              if (disabled) return;
              setCountry?.(e.target.value);
              setCity?.("");
              setState?.("");
              openSugg("country");
            }}
            onFocus={() => openSugg("country")}
            disabled={disabled}
          />
          {country && sugg.country.length > 0 && (
            <Dropdown
              items={sugg.country}
              onSelect={(item) => {
                setCountry?.(item);
                setCity?.("");
                setState?.("");
                setSugg((s) => ({ ...s, country: [] }));
              }}
            />
          )}
        </div>
      </div>

      {/* City */}
      <div className="col-lg-4">
        <div className="mb-3 position-relative" ref={cityRef}>
          <input
            type="text"
            className="form-control fw-light p-3"
            placeholder={t("City")}
            value={city || ""}
            onChange={(e) => {
              if (disabled) return;
              setCity?.(e.target.value);
              setState?.("");
              openSugg("city");
            }}
            onFocus={() => openSugg("city")}
            disabled={disabled}
          />
          {city && sugg.city.length > 0 && (
            <Dropdown
              items={sugg.city}
              onSelect={(item) => {
                setCity?.(item);
                setState?.("");
                setSugg((s) => ({ ...s, city: [] }));
              }}
            />
          )}
        </div>
      </div>

      {/* State / Region */}
      <div className="col-lg-4">
        <div className="mb-3 position-relative" ref={stateRef}>
          <input
            type="text"
            className="form-control fw-light p-3"
            placeholder={t("State")}
            value={region || ""}
            onChange={(e) => {
              if (disabled) return;
              setState?.(e.target.value);
              openSugg("state");
            }}
            onFocus={() => openSugg("state")}
            disabled={disabled}
          />
          {region && sugg.state.length > 0 && (
            <Dropdown
              items={sugg.state}
              onSelect={(item) => {
                setState?.(item);
                setSugg((s) => ({ ...s, state: [] }));
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}
